import { HubConnectionState } from '@microsoft/signalr';
import { nanoid } from 'nanoid';
import { createContext, FCWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgentStateData, AgentTodayData, BotSegment, BotStepTodayData, QueueNowData, QueueTodayData, WorkItem } from '../models/reports.external';
import { ITeamReadModel } from '../models/team';
import { useGetTeamsByMemberQuery } from '../redux/apis/teamApi';
import { getEnvSettings } from '../app/hostBasedSettings';
import useSignalrHub, { Method } from '../app/signalr/useSignalrHub';
import { objectValues } from '../utils/objectUtils';
import useUserInfo from '../shared/hooks/useUserInfo';

const emptyArray: unknown[] = [];

interface IHandlers {
    receiveTodaysQueueData?(data: QueueTodayData): void;
    receiveNowQueueData?(data: QueueNowData): void;
    receiveTodaysAgentData?(data: AgentTodayData): void;
    receiveAgentStates?(data: AgentStateData[]): void;
    receiveWorkItems?(data: WorkItem[]): void;
    receiveTodaysBotStepData(data: BotStepTodayData[]): void;
    receiveBotSegments(data: BotSegment[]): void;
}

interface IContextValue {
    subscribe(handlers: Partial<IHandlers>): string;
    unsubscribe(subscriberId: string): void;
    myTeams: ITeamReadModel[];
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const SignalrDataContext = createContext<IContextValue>(undefined!);

const SignalrDataProvider: FCWithChildren = ({ children }) => {
    const baseUrl = getEnvSettings().apiBaseUrl;

    const [subscribers, setSubscribers] = useState<Record<string, Partial<IHandlers>>>({});

    // subscribersRef used not to tear down and rebuild SignalR connection every time on subscriber change
    const subscribersRef = useRef(subscribers);
    subscribersRef.current = subscribers;

    const stopConnectionRef = useRef<NodeJS.Timeout>();

    //#region [1] Fetch data for SignalR params
    const { id: userId } = useUserInfo();
    const { data: myTeams = emptyArray as ITeamReadModel[] } = useGetTeamsByMemberQuery(userId ?? '', { skip: !userId });

    const { isInitiated, queueIds, agentIds } = useMemo(
        () => {
            if (myTeams.length === 0)
                return { isInitiated: false, queueIds: [], agentIds: [] };

            const myQueues = myTeams.reduce((coll, curr) => [...coll, ...curr.queueIds], [] as string[]);
            const myTeamMembers = myTeams.reduce((coll, curr) => [...coll, ...curr.memberIds], [] as string[]);

            return {
                isInitiated: true,
                queueIds: Array.from(new Set(myQueues)),
                agentIds: Array.from(new Set(myTeamMembers)),
            };
        },
        [myTeams],
    );
    //#endregion

    //#region [2] Define SignalR handlers
    const receiveTodaysQueueData = useCallback(
        (data: QueueTodayData) => objectValues(subscribersRef.current).forEach(handlers => handlers.receiveTodaysQueueData?.(data)),
        [],
    );

    const receiveNowQueueData = useCallback(
        (data: QueueNowData) => objectValues(subscribersRef.current).forEach(handlers => handlers.receiveNowQueueData?.(data)),
        [],
    );

    const receiveTodaysAgentData = useCallback(
        (data: AgentTodayData) => objectValues(subscribersRef.current).forEach(handlers => handlers.receiveTodaysAgentData?.(data)),
        [],
    );

    const receiveAgentStates = useCallback(
        (data: AgentStateData[]) => objectValues(subscribersRef.current).forEach(handlers => handlers.receiveAgentStates?.(data)),
        [],
    );

    const receiveWorkItems = useCallback(
        (data: WorkItem[]) => objectValues(subscribersRef.current).forEach(handlers => handlers.receiveWorkItems?.(data)),
        [],
    );

    const receiveTodaysBotStepData = useCallback(
        (data: BotStepTodayData[]) => objectValues(subscribersRef.current).forEach(handlers => handlers.receiveTodaysBotStepData?.(data)),
        [],
    );

    const receiveBotSegments = useCallback(
        (data: BotSegment[]) => objectValues(subscribersRef.current).forEach(handlers => handlers.receiveBotSegments?.(data)),
        [],
    );
    //#endregion

    //#region [3] Define SignalR ADP and LDP methods using handlers
    const todayHandlers = useMemo<Method[]>(
        () => [
            { name: 'ReceiveTodaysAgentReportData', handler: receiveTodaysAgentData },
            { name: 'ReceiveTodaysAllBotStepReportData', handler: receiveTodaysBotStepData },
            { name: 'ReceiveTodaysQueueData', handler: receiveTodaysQueueData },
        ],
        [receiveTodaysAgentData, receiveTodaysBotStepData, receiveTodaysQueueData],
    );

    const liveHandlers = useMemo<Method[]>(
        () => [
            { name: 'ReceiveBotSegments', handler: receiveBotSegments },
            { name: 'ReceiveQueueData', handler: receiveNowQueueData },
            { name: 'ReceiveTenantAgentStates', handler: receiveAgentStates },
            { name: 'ReceiveWorkItemsWithConversations', handler: receiveWorkItems },
        ],
        [receiveAgentStates, receiveBotSegments, receiveNowQueueData, receiveWorkItems],
    );
    //#endregion

    //#region [4] Connect to SignalR hubs
    const { start: startToday, stop: stopToday, state: todayHubState } = useSignalrHub(
        `${baseUrl}/aggregations/today/hub`,
        { queueIds, agentIds, subscribeToAllBotSteps: ['yesPlease'] },
        todayHandlers,
    );

    const { start: startLive, stop: stopLive, state: liveHubState } = useSignalrHub(
        `${baseUrl}/live/hub`,
        { queueIds },
        liveHandlers,
    );

    const isDisconnectedRef = useRef(true);
    isDisconnectedRef.current = todayHubState === HubConnectionState.Disconnected && liveHubState === HubConnectionState.Disconnected;
    //#endregion

    //#region [5] Define SignalR start/stop methods
    const start = useCallback(
        () => {
            startToday();
            startLive();
        },
        [startLive, startToday],
    );

    const stop = useCallback(
        () => {
            stopToday();
            stopLive();
        },
        [stopLive, stopToday],
    );
    //#endregion

    //#region [6] Manage subscriptions
    const subscribe = useCallback(
        (handlers: Partial<IHandlers>) => {
            const subscriberId = nanoid();
            setSubscribers(curr => ({ ...curr, [subscriberId]: handlers }));
            return subscriberId;
        },
        [],
    );

    const unsubscribe = useCallback(
        (subscriberId: string) => setSubscribers(curr => {
            const newSubscribers = { ...curr };
            delete newSubscribers[subscriberId];
            return newSubscribers;
        }),
        [],
    );

    useEffect(
        () => {
            if (isInitiated && Object.keys(subscribers).length > 0) {
                if (stopConnectionRef.current) {
                    clearTimeout(stopConnectionRef.current);
                    stopConnectionRef.current = undefined;
                }
                start();
            }
            else if (!isDisconnectedRef.current && stopConnectionRef.current === undefined) {
                stopConnectionRef.current = setTimeout(stop, 10000);
            }
        },
        [isInitiated, start, stop, subscribers], // start, stop are static
    );
    //#endregion

    return (
        <SignalrDataContext.Provider value={{ subscribe, unsubscribe, myTeams }}>
          {children}
        </SignalrDataContext.Provider>
      );
};

export default SignalrDataProvider;